import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import useUserId from "../shared/hooks/useUserId";
import useGameAndVersion from "../shared/hooks/useGameAndVersion";

import {
  createMatchStorage,
  MatchStorage,
  MatchPlayer,
  VersionChannel,
} from "./services/MatchStorage";
import { LoadingScreen } from "./components/LoadingScreen";
import { GameModuleMount } from "./components/GameModuleMount";
import { useGameUpdates } from "./hooks/useGameUpdates";
import { Game } from "../shared/api/client";

export default function Player() {
  const { game: queryGame, version } = useParams();

  const [loaded, setLoaded] = useState(false);
  const [uid, loading] = useUserId();
  const { matchId } = useParams();
  const [matchStorage, setMatchStorage] = useState<MatchStorage | undefined>();
  const [module, setModule] = useState<any>();
  const [players, setPlayers] = useState<MatchPlayer[] | undefined>();
  const [gameName, setGameName] = useState<string | undefined>(queryGame);
  const [gameMajorVersion, setGameMajorVersion] = useState<number | undefined>(
    version ? parseInt(version, 10) : undefined
  );
  const [executionMode, setExecutionMode] = useState<'client' | 'server'>('client');
  const [gameOptions, setGameOptions] = useState<Record<string, string> | undefined>();

  // The match's minor version channel (read from match data, not user-selectable)
  const [minorVersionChannel, setMinorVersionChannel] = useState<VersionChannel>("stable");

  // Get game data and load the correct version based on the match's channel
  const { gameVersion } = useGameAndVersion(
    gameName,
    null,
    gameMajorVersion,
    minorVersionChannel
  );

  // Track the currently loaded version string for change detection
  const loadedVersionRef = useRef<string | undefined>(undefined);

  // Update the ref when gameVersion changes
  useEffect(() => {
    if (gameVersion?.version) {
      loadedVersionRef.current = gameVersion.version;
    }
  }, [gameVersion?.version]);

  // Handle game updates - reload if the version we should be using has changed
  const handleGameUpdate = useCallback(
    (updatedGame: Game) => {
      if (gameMajorVersion === undefined || !loadedVersionRef.current) {
        return;
      }

      // Get the version string for our major version + channel from the updated game
      const versionSettings = updatedGame.versions[gameMajorVersion];
      if (!versionSettings) {
        return;
      }

      // Determine which version we should now be loading
      const newVersionString =
        (minorVersionChannel === 'beta' ? versionSettings.beta : versionSettings.stable) ??
        versionSettings.stable;

      // If the version changed, reload to pick up the new version
      if (newVersionString && newVersionString !== loadedVersionRef.current) {
        console.log(
          `Game version changed from ${loadedVersionRef.current} to ${newVersionString}, reloading...`
        );
        window.location.reload();
      }
    },
    [gameMajorVersion, minorVersionChannel]
  );

  // Subscribe to game updates for version change detection
  useGameUpdates(gameName, handleGameUpdate);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !uid) {
      window.localStorage.setItem("postLoginUrl", document.location.href);
      window.location.href = "/spa/login";
    }
  }, [uid, loading]);

  // Load match data
  useEffect(() => {
    if (!matchId) {
      return;
    }

    const storage = createMatchStorage(matchId);
    setMatchStorage(storage);

    storage.readGameFastData().then((d) => {
      document.title = `${d.name} - ${d.game}`;
      setGameName(d.game);
      setGameMajorVersion(d.majorVersion);
      if (d.minorVersionChannel) {
        setMinorVersionChannel(d.minorVersionChannel);
      }
    });

    storage.readDocument().then((d) => {
      document.title = `${d.name} - ${d.gameName}`;

      if (d.status !== "active" && d.status !== "finished") {
        storage.dispose();
        window.location.href = `/spa/match/${matchId}`;
        return;
      }

      setPlayers(d.players);
      setGameName(d.game);
      setGameMajorVersion(d.majorVersion);
      setExecutionMode(d.executionMode || 'client');
      setGameOptions(d.options);
      if (d.minorVersionChannel) {
        setMinorVersionChannel(d.minorVersionChannel);
      }
    });

    return () => {
      storage.dispose();
    };
  }, [matchId]);

  // Load game module
  useEffect(() => {
    if (!gameVersion) {
      setModule(undefined);
      return;
    }

    import(/* @vite-ignore */ /* webpackIgnore: true */ gameVersion?.webEntry)
      .then((module: any) => {
        setModule(module);
      })
      .catch((err: any) => {
        console.error("Failed to load module:", err);
      });
  }, [gameVersion]);

  // Ready to mount the game module when all data is loaded
  // For server mode, we don't need matchStorage
  const readyToMount = module && players && uid && matchId && (
    executionMode === 'server' || matchStorage
  );

  return (
    <>
      {/* Show loading screen until game is ready */}
      {!loaded && (
        <LoadingScreen
          backgroundColor={gameVersion?.backgroundColor}
          foregroundColor={gameVersion?.foregroundColor}
        />
      )}

      {/* Render the game module mount point - hidden until loaded */}
      {readyToMount && (
        <GameModuleMount
          module={module}
          matchId={matchId!}
          matchStorage={matchStorage ?? null}
          userId={uid}
          players={players}
          executionMode={executionMode}
          gameOptions={gameOptions}
          onLoaded={() => setLoaded(true)}
          onLoadProgress={(totalPanels, panelsLoaded) => {
            setLoaded(totalPanels > 0 && totalPanels === panelsLoaded);
          }}
        />
      )}
    </>
  );
}
